import {
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import { DatePicker } from '@mui/x-date-pickers';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useEffect, Fragment, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import DeleteIcon from '@/assets/icons/button-decorators/delete.svg?react';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { MarkdownSyntaxModal } from '@/components/modals/MarkdownSyntaxModal';
import { ButtonLink } from '@/components/router/ButtonLink';
import { SystemNotification } from '@/components/system-notification/SystemNotification';
import type { UpdateSystemNotificationDto } from '@/modules/api';
import {
  backendApiSystemNotificationsFindOneOptions,
  backendApiSystemNotificationsRemoveMutation,
  backendApiSystemNotificationsUpdateMutation,
} from '@/modules/api/@tanstack/react-query.gen';

export const Route = createFileRoute('/verwaltung/_global/benachrichtigungen/$systemNotificationId')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const { systemNotificationId } = Route.useParams();
  const { data: systemNotification, isLoading } = useQuery(
    backendApiSystemNotificationsFindOneOptions({ path: { id: systemNotificationId } }),
  );
  const navigate = useNavigate();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [showGuide, setShowGuide] = useState<boolean>(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { errors, isDirty },
  } = useForm<UpdateSystemNotificationDto>();

  const { mutateAsync, isPending, error: mutateError } = useMutation(backendApiSystemNotificationsUpdateMutation());
  const {
    mutateAsync: removeMutate,
    isPending: isRemovePending,
    error: removeError,
  } = useMutation(backendApiSystemNotificationsRemoveMutation());

  useEffect(() => {
    if (systemNotification)
      reset({
        title: systemNotification.title,
        content: systemNotification.content,
        validTo: systemNotification.validTo,
        validFrom: systemNotification.validFrom,
      });
  }, [systemNotification, reset]);

  const previewContent = watch('content');
  const previewTitle = watch('title');

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!systemNotification) {
    return <Typography color="danger">Benachrichtigung nicht gefunden.</Typography>;
  }

  return (
    <Fragment>
      <ConfirmationModal
        content="Möchten Sie diese Benachrichtigung wirklich löschen?"
        onAbort={() => setConfirmDeleteOpen(false)}
        open={confirmDeleteOpen}
        title="Benachrichtigung löschen"
        onConfirm={async () => {
          setConfirmDeleteOpen(false);
          await removeMutate(
            { path: { id: systemNotificationId } },
            { onSuccess: async () => navigate({ to: '/verwaltung/benachrichtigungen' }) },
          );
        }}
      />
      <Grid
        container
        component="form"
        spacing={2}
        onSubmit={handleSubmit(async (data) =>
          mutateAsync(
            {
              path: {
                id: systemNotificationId,
              },
              body: data,
            },
            { onSuccess: async () => navigate({ to: '/verwaltung/benachrichtigungen' }) },
          ),
        )}
      >
        <Grid xs={6}>
          <FormControl error={!!errors.title}>
            <FormLabel>Titel</FormLabel>
            <Input variant="outlined" {...register('title', { required: true, minLength: 1 })} />
            {errors.title ? <FormHelperText>Pflichtfeld</FormHelperText> : null}
          </FormControl>
        </Grid>

        <Grid xs={3}>
          <Controller
            control={control}
            name="validFrom"
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <FormControl error={!!errors.validFrom}>
                <FormLabel>Gültig von</FormLabel>
                <DatePicker
                  format="DD.MM.YYYY"
                  value={value ? dayjs(value) : null}
                  onChange={(newValue) => {
                    onChange(newValue ? newValue.toISOString() : undefined);
                  }}
                  slotProps={{
                    textField: {
                      error: !!errors.validFrom,
                    },
                    field: {
                      clearable: true,
                    },
                  }}
                />
                {errors.validFrom ? <FormHelperText>Pflichtfeld</FormHelperText> : null}
              </FormControl>
            )}
          />
        </Grid>

        <Grid xs={3}>
          <Controller
            control={control}
            name="validTo"
            render={({ field: { value, onChange } }) => (
              <FormControl error={!!errors.validTo}>
                <FormLabel>Gültig bis</FormLabel>
                <DatePicker
                  format="DD.MM.YYYY"
                  value={value ? dayjs(value) : null}
                  onChange={(newValue) => {
                    onChange(newValue ? newValue.toISOString() : undefined);
                  }}
                  slotProps={{
                    textField: {
                      error: !!errors.validTo,
                    },
                    field: {
                      clearable: true,
                    },
                  }}
                />
                {errors.validTo ? <FormHelperText>{errors.validTo.message ?? 'Pflichtfeld'}</FormHelperText> : null}
              </FormControl>
            )}
            rules={{
              required: true,
              validate: (value) => {
                const validFrom = getValues('validFrom');
                if (validFrom && value && !dayjs(value).isAfter(dayjs(validFrom))) {
                  return 'Muss nach "Gültig von" liegen';
                }

                return true;
              },
            }}
          />
        </Grid>

        <Grid xs={12}>
          <FormControl error={!!errors.content}>
            <FormLabel>
              <Stack alignItems="center" direction="row" gap={2}>
                Inhalt
                <Button onClick={() => setShowGuide(true)} variant="plain">
                  Markdown Syntax
                </Button>
              </Stack>
            </FormLabel>
            <Textarea
              variant="outlined"
              slotProps={{
                textarea: {
                  minRows: 10,
                  ...register('content', { required: true, minLength: 1 }),
                },
              }}
            />
            {errors.content ? <FormHelperText>Pflichtfeld</FormHelperText> : null}
            {showGuide ? <MarkdownSyntaxModal onClose={() => setShowGuide(false)} /> : null}
          </FormControl>
        </Grid>
        <Grid xs={12}>
          <Stack alignItems="center" direction="row" gap={2}>
            <ButtonLink color="neutral" to="/verwaltung/benachrichtigungen" variant="outlined">
              abbrechen
            </ButtonLink>
            <Button
              color="danger"
              disabled={isRemovePending}
              loading={isRemovePending}
              onClick={() => setConfirmDeleteOpen(true)}
              startDecorator={<DeleteIcon />}
              variant="outlined"
            >
              löschen
            </Button>
            <Button disabled={!isDirty || isPending} loading={isPending} type="submit">
              speichern
            </Button>
            {mutateError ? <Typography color="danger">{mutateError.message}</Typography> : null}
            {removeError ? <Typography color="danger">{removeError.message}</Typography> : null}
          </Stack>
        </Grid>
        <Grid xs={8}>
          <Stack gap={2}>
            <FormLabel>Vorschau</FormLabel>
            {previewTitle && previewContent ? (
              <SystemNotification
                preview
                systemNotification={{ title: previewTitle, content: previewContent, id: systemNotificationId }}
              />
            ) : (
              <Typography>Bitte Titel und Inhalt eingeben</Typography>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Fragment>
  );
}
